import { Injectable } from '@angular/core';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { AuthService } from '../auth.service';
import { ProjectOrganizationNotificationHandlerService } from './project-organization-notification-handler.service';
import { CommunicationActivityNotificationHandlerService } from './communication-activity-notification-handler.service';
import { NotificationMessage, NotificationObject, UserProfileSettings } from './notification.types';

@Injectable({
  providedIn: 'root',
})
export class NotificationMessageHelperService {
  constructor(
    private readonly translatePipe: TranslatePipe,
    private readonly authService: AuthService,
    private readonly projectOrgHandler: ProjectOrganizationNotificationHandlerService,
    private readonly communicationHandler: CommunicationActivityNotificationHandlerService
  ) {}

  /**
   * Get message for display notification
   * @param code - Notification code
   * @param object - Notification object with data
   * @param userProfileSettings - User profile settings for date/time formatting
   */
  public getMessage(
    code: string,
    object: NotificationObject | null = null,
    userProfileSettings?: UserProfileSettings
  ): NotificationMessage | null {
    if (!object) {
      return null;
    }

    const roles: { newRole?: string; oldRole?: string; invitedRoleName?: string } = this.prepareRoles(object);
    let notificationCode: string = `notification${code}`;
    const originalCode: string = notificationCode;
    const authUserName: string | null = this.authService.getAuthUserName();

    notificationCode = this.adjustNotificationCode(code, notificationCode, object, authUserName);

    const translatedNotification: string = this.translatePipe.transform(notificationCode);
    const message: NotificationMessage = {
      label: translatedNotification,
      action: {},
    };

    try {
      this.processNotificationByCode(code, notificationCode, originalCode, object, message, roles, userProfileSettings);
    } catch {
      // handle notification processing errors
    }

    // Check if translation failed - label should be different from both codes
    if (message.label === notificationCode || message.label === originalCode) {
      return null;
    }

    return message;
  }

  private prepareRoles(object: NotificationObject): {
    newRole?: string;
    oldRole?: string;
    invitedRoleName?: string;
  } {
    return {
      newRole: object.newRole ? this.translatePipe.translateRole(object.newRole) : undefined,
      oldRole: object.oldRole ? this.translatePipe.translateRole(object.oldRole) : undefined,
      invitedRoleName: object.roleName ? this.translatePipe.translateRole(object.roleName) : undefined,
    };
  }

  private adjustNotificationCode(
    code: string,
    notificationCode: string,
    object: NotificationObject,
    authUserName: string | null
  ): string {
    if (code === 'ANNOTATION_REPLIED') {
      if (object.notifiedTargetUsername === authUserName) {
        return `${notificationCode}_YOUR`;
      } else if (object.notifiedByUsername === object.notifiedTargetUsername) {
        return `${notificationCode}_SELF`;
      }
    } else if (object.notifiedByUsername === authUserName) {
      return `${notificationCode}_SELF`;
    } else if (object.notifiedTargetUsername === authUserName) {
      return `${notificationCode}_YOUR`;
    }
    return notificationCode;
  }

  private processNotificationByCode(
    code: string,
    notificationCode: string,
    originalCode: string,
    object: NotificationObject,
    message: NotificationMessage,
    roles: { newRole?: string; oldRole?: string; invitedRoleName?: string },
    userProfileSettings?: UserProfileSettings
  ): void {
    const authUserName: string | null = this.authService.getAuthUserName();

    if (this.handleProjectNotifications(code, notificationCode, object, message)) {
      return;
    }
    if (this.handleSubsectionNotifications(code, originalCode, object, message)) {
      return;
    }
    if (this.handleCommentNotifications(code, notificationCode, originalCode, object, message)) {
      return;
    }
    if (this.handleVoteNotifications(code, originalCode, object, message)) {
      return;
    }
    if (this.handleFileNotifications(code, notificationCode, object, message)) {
      return;
    }
    if (this.handleShareNotifications(code, originalCode, object, message)) {
      return;
    }
    if (this.handleConversationNotifications(code, notificationCode, originalCode, object, message, authUserName)) {
      return;
    }
    if (this.handleEventNotifications(code, notificationCode, object, message, userProfileSettings)) {
      return;
    }
    if (this.handleWorkspaceNotifications(code, originalCode, object, message, roles)) {
      return;
    }
    if (this.handleCompanyNotifications(code, notificationCode, object, message, roles)) {
      return;
    }
    if (this.handleExtTaskNotifications(code, notificationCode, object, message, userProfileSettings)) {
      return;
    }
    if (this.handleSubtitleNotifications(code, originalCode, object, message)) {
      return;
    }
    if (this.handleAiProcessingNotifications(code, originalCode, object, message)) {
      return;
    }
  }

  private handleProjectNotifications(
    code: string,
    notificationCode: string,
    object: NotificationObject,
    message: NotificationMessage
  ): boolean {
    switch (code) {
      case 'PROJECT_CREATED':
      case 'PROJECT_UPDATED':
      case 'PROJECT_ARCHIVED':
      case 'PROJECT_DELETED':
        this.projectOrgHandler.handleProjectNotifications(notificationCode, object, message);
        return true;
      case 'PROJECT_INVITATION':
      case 'PROJECT_USER_INVITED':
      case 'PROJECT_USER_REMOVED':
        this.projectOrgHandler.handleProjectUserNotifications(notificationCode, object, message);
        return true;
      case 'PROJECT_USER_ROLES_UPDATED':
        this.projectOrgHandler.handleProjectUserRolesUpdated(notificationCode, object, message);
        return true;
      case 'PROJECT_STATUS_UPDATED':
        this.projectOrgHandler.handleProjectStatusUpdated(notificationCode, object, message);
        return true;
      case 'CAMPAIGN_STATUS_UPDATED':
        this.projectOrgHandler.handleCampaignStatusUpdated(notificationCode, object, message);
        return true;
      case 'ASSET_LIST_STATUS_UPDATED':
        this.projectOrgHandler.handleAssetListStatusUpdated(notificationCode, object, message);
        return true;
      default:
        return false;
    }
  }

  private handleSubsectionNotifications(
    code: string,
    originalCode: string,
    object: NotificationObject,
    message: NotificationMessage
  ): boolean {
    switch (code) {
      case 'PROJECT_SUBSECTION_CREATED':
      case 'PROJECT_SUBSECTION_DATE_CHANGED':
        this.projectOrgHandler.handleSubsectionCreated(originalCode, object, message);
        return true;
      case 'PROJECT_SUBSECTION_DELETED':
        this.projectOrgHandler.handleSubsectionDeleted(originalCode, object, message);
        return true;
      case 'PROJECT_SUBSECTION_ITEM_CREATED':
        this.projectOrgHandler.handleSubsectionItemCreated(originalCode, object, message);
        return true;
      case 'PROJECT_SUBSECTION_ITEM_APPROVED':
        this.projectOrgHandler.handleSubsectionItemApproved(originalCode, object, message);
        return true;
      default:
        return false;
    }
  }

  private handleCommentNotifications(
    code: string,
    notificationCode: string,
    originalCode: string,
    object: NotificationObject,
    message: NotificationMessage
  ): boolean {
    switch (code) {
      case 'COMMENT_CREATED':
      case 'COMMENT_DELETED':
      case 'COMMENT_EDITED':
      case 'ANNOTATION_CREATED':
      case 'ANNOTATION_DELETED':
      case 'ANNOTATION_EDITED':
        this.communicationHandler.handleCommentNotifications(originalCode, object, message);
        return true;
      case 'COMMENT_REPLIED':
      case 'ANNOTATION_TAGGED':
      case 'ANNOTATION_REPLIED':
        this.communicationHandler.handleAnnotationTaggedAndCommentReplies(notificationCode, object, message);
        return true;
      default:
        return false;
    }
  }

  private handleVoteNotifications(
    code: string,
    originalCode: string,
    object: NotificationObject,
    message: NotificationMessage
  ): boolean {
    switch (code) {
      case 'VOTE_YES':
      case 'VOTE_NO':
      case 'VOTE_MAYBE':
        this.communicationHandler.handleVoteNotifications(originalCode, object, message);
        return true;
      case 'VOTE_CANCELED':
        this.communicationHandler.handleVoteCanceled(originalCode, object, message);
        return true;
      default:
        return false;
    }
  }

  private handleFileNotifications(
    code: string,
    notificationCode: string,
    object: NotificationObject,
    message: NotificationMessage
  ): boolean {
    switch (code) {
      case 'FILE_UPLOADED':
        this.communicationHandler.handleFileUploaded(notificationCode, object, message);
        return true;
      case 'FILE_BATCH_UPLOADED':
        this.communicationHandler.handleFileBatchUploaded(notificationCode, object, message);
        return true;
      case 'FILE_DELETED':
        this.communicationHandler.handleFileDeleted(notificationCode, object, message);
        return true;
      case 'FILE_DOWNLOADED':
        this.communicationHandler.handleFileDownloaded(notificationCode, object, message);
        return true;
      case 'FILE_MODIFIED':
      case 'FILE_MOVED':
      case 'FILE_RENAMED':
      case 'FILE_COPIED':
      case 'DIRECTORY_CREATED':
      case 'DIRECTORY_RENAMED':
      case 'DIRECTORY_DELETED':
        this.communicationHandler.handleDriveEvent(notificationCode, object, message);
        return true;
      default:
        return false;
    }
  }

  private handleShareNotifications(
    code: string,
    originalCode: string,
    object: NotificationObject,
    message: NotificationMessage
  ): boolean {
    switch (code) {
      case 'SHARE_INVITE_SENT':
        this.communicationHandler.handleShareInviteSent(originalCode, object, message);
        return true;
      default:
        return false;
    }
  }

  private handleConversationNotifications(
    code: string,
    notificationCode: string,
    originalCode: string,
    object: NotificationObject,
    message: NotificationMessage,
    authUserName: string | null
  ): boolean {
    switch (code) {
      case 'CONVERSATION_CALL_MISSED':
        this.communicationHandler.handleConversationCallMissed(originalCode, object, message);
        return true;
      case 'INVITATION_ACCEPTED':
      case 'INVITATION_FIRST_ACCEPTED':
        this.communicationHandler.handleInvitationAccepted(originalCode, object, message);
        return true;
      case 'PROJECT_DATE':
        this.projectOrgHandler.handleProjectDate(originalCode, object, message);
        return true;
      case 'INVITATION_DECLINED':
        this.communicationHandler.handleInvitationDeclined(notificationCode, object, message);
        return true;
      case 'VERIFICATION_STATUS':
        this.communicationHandler.handleVerificationStatus(notificationCode, object, message);
        return true;
      case 'CONVERSATION_CREATED':
      case 'CONVERSATION_UPDATED':
      case 'CONVERSATION_ARCHIVED':
      case 'CONVERSATION_DELETED':
        this.communicationHandler.handleConversationNotifications(notificationCode, object, message);
        return true;
      case 'CONVERSATION_USER_ADDED':
      case 'CONVERSATION_USER_REMOVED':
        this.communicationHandler.handleConversationUserNotifications(notificationCode, object, message);
        return true;
      case 'CONVERSATION_MESSAGE_SENT':
        this.communicationHandler.handleConversationMessageSent(notificationCode, object, message, authUserName);
        return true;
      default:
        return false;
    }
  }

  private handleEventNotifications(
    code: string,
    notificationCode: string,
    object: NotificationObject,
    message: NotificationMessage,
    userProfileSettings?: UserProfileSettings
  ): boolean {
    switch (code) {
      case 'EVENT_CREATED':
      case 'EVENT_UPDATED':
      case 'EVENT_ARCHIVED':
      case 'EVENT_DELETED':
        this.communicationHandler.handleEventNotifications(notificationCode, object, message, userProfileSettings);
        return true;
      case 'EVENT_USER_ADDED':
      case 'EVENT_USER_REMOVED':
        this.communicationHandler.handleEventUserNotifications(notificationCode, object, message, userProfileSettings);
        return true;
      default:
        return false;
    }
  }

  private handleWorkspaceNotifications(
    code: string,
    originalCode: string,
    object: NotificationObject,
    message: NotificationMessage,
    roles: { newRole?: string; oldRole?: string; invitedRoleName?: string }
  ): boolean {
    switch (code) {
      case 'WORKSPACE_ADDED_USER':
      case 'WORKSPACE_DELETED_USER':
      case 'WORKSPACE_EDITED':
      case 'WORKSPACE_CREATED':
      case 'WORKSPACE_DELETED':
        this.projectOrgHandler.handleWorkspaceNotifications(originalCode, object, message, roles.invitedRoleName);
        return true;
      case 'WORKSPACE_MOVED_PROJECT':
        this.projectOrgHandler.handleWorkspaceMovedProject(originalCode, object, message);
        return true;
      case 'WORKSPACE_EDITED_USER':
        this.projectOrgHandler.handleWorkspaceEditedUser(originalCode, object, message, roles.oldRole, roles.newRole);
        return true;
      default:
        return false;
    }
  }

  private handleCompanyNotifications(
    code: string,
    notificationCode: string,
    object: NotificationObject,
    message: NotificationMessage,
    roles: { newRole?: string; oldRole?: string; invitedRoleName?: string }
  ): boolean {
    switch (code) {
      case 'CHANGED_COMPANY_ROLE':
        this.projectOrgHandler.handleChangedCompanyRole(
          notificationCode,
          object,
          message,
          roles.oldRole,
          roles.newRole
        );
        return true;
      case 'INVITED_TO_COMPANY':
        this.projectOrgHandler.handleInvitedToCompany(notificationCode, object, message);
        return true;
      case 'COMPANY_OWNERSHIP_TRANSFERRED':
        this.projectOrgHandler.handleCompanyOwnershipTransferred(notificationCode, object, message);
        return true;
      case 'DELETED_FROM_COMPANY':
      case 'DEACTIVATED_FROM_COMPANY':
        this.projectOrgHandler.handleCompanyUserRemoved(notificationCode, object, message);
        return true;
      default:
        return false;
    }
  }

  private handleExtTaskNotifications(
    code: string,
    notificationCode: string,
    object: NotificationObject,
    message: NotificationMessage,
    userProfileSettings?: UserProfileSettings
  ): boolean {
    switch (code) {
      case 'EXT_TASK_STATUS_UPDATED':
      case 'EXT_TASK_USER_ADDED':
      case 'EXT_TASK_USER_REMOVED':
      case 'EXT_TASK_PRIORITY_UPDATED':
      case 'EXT_TASK_DUE_DATE_UPDATED':
      case 'EXT_TASK_SUBTASK_UPDATED':
      case 'EXT_TASK_SUBTASK_USER_ADDED':
      case 'EXT_TASK_SUBTASK_USER_REMOVED':
      case 'EXT_TASK_DELETED':
      case 'EXT_TASK_DESCRIPTION_UPDATED':
      case 'EXT_TASK_DESCRIPTION_MENTION':
      case 'EXT_TASK_PARTICIPANT_ADDED':
      case 'EXT_TASK_PARTICIPANT_REMOVED':
      case 'EXT_TASK_SUBTASK_PARTICIPANT_ADDED':
      case 'EXT_TASK_SUBTASK_PARTICIPANT_REMOVED':
        this.communicationHandler.handleExtTaskNotification(notificationCode, object, message, userProfileSettings);
        return true;
      default:
        return false;
    }
  }

  private handleSubtitleNotifications(
    code: string,
    originalCode: string,
    object: NotificationObject,
    message: NotificationMessage
  ): boolean {
    switch (code) {
      case 'TRANSCRIBING_STARTED':
      case 'TRANSCRIBING_FINISHED':
      case 'TRANSLATION_STARTED':
      case 'TRANSLATION_DELETED':
      case 'TRANSLATION_FINISHED':
      case 'BURN_SUBTITLE_STARTED':
      case 'BURN_SUBTITLE_FINISHED':
      case 'SUBTITLE_EDITED':
      case 'BURN_SUBTITLE_DELETED':
        this.communicationHandler.handleSubtitleNotifications(originalCode, object, message);
        return true;
      default:
        return false;
    }
  }

  private handleAiProcessingNotifications(
    code: string,
    originalCode: string,
    object: NotificationObject,
    message: NotificationMessage
  ): boolean {
    switch (code) {
      case 'AI_PROCESSING_MESSAGE':
        this.communicationHandler.handleAiProcessingNotifications(originalCode, object, message);
        return true;
      default:
        return false;
    }
  }
}
